/**
 * URLStateManager
 * Parses sort/filter from URL, generates cache keys, detects URL changes
 */
class URLStateManager {
  constructor() {
    this.currentParams = this.parse();
  }

  /**
   * Sort key mapping from URL params to GraphQL enum values
   */
  static SORT_KEY_MAP = {
    "manual": { sortKey: "MANUAL", reverse: false },
    "best-selling": { sortKey: "BEST_SELLING", reverse: false },
    "title-asc": { sortKey: "TITLE", reverse: false },
    "title-desc": { sortKey: "TITLE", reverse: true },
    "price-asc": { sortKey: "PRICE", reverse: false },
    "price-desc": { sortKey: "PRICE", reverse: true },
    "created-desc": { sortKey: "CREATED", reverse: true },
    "created-asc": { sortKey: "CREATED", reverse: false },
  };

  /**
   * Parse current URL and extract sort/filter parameters
   * @param {string} [url] - URL to parse, defaults to current location
   * @returns {Object} Parsed parameters
   */
  parse(url = window.location.href) {
    const parsed = new URL(url, window.location.origin);
    const params = new URLSearchParams(parsed.search);

    const result = {
      pathname: parsed.pathname,
      collectionHandle: this.extractCollectionHandle(parsed.pathname),
      sort: params.get("sort_by") || null,
      filters: [],
      page: parseInt(params.get("page")) || 1,
      rawParams: params,
    };

    // Extract filter parameters (filter.*)
    for (const [key, value] of params.entries()) {
      if (key.startsWith("filter.")) {
        const filterName = key.replace("filter.", "");
        result.filters.push({ name: filterName, value });
      }
    }

    return result;
  }

  /**
   * Extract collection handle from pathname
   * @param {string} pathname
   * @returns {string|null}
   */
  extractCollectionHandle(pathname) {
    const match = pathname.match(/\/collections\/([^\/]+)/);
    return match ? match[1] : null;
  }

  /**
   * Get GraphQL sort parameters from URL sort value
   * @param {string} [sortValue] - URL sort value, defaults to current
   * @returns {Object} { sortKey, reverse }
   */
  getGraphQLSort(sortValue) {
    const sort = sortValue || this.currentParams.sort;
    return (
      URLStateManager.SORT_KEY_MAP[sort] || {
        sortKey: "COLLECTION_DEFAULT",
        reverse: false,
      }
    );
  }

  /**
   * Convert URL filters to GraphQL ProductFilter format
   * @returns {Array} GraphQL filters array
   */
  getGraphQLFilters() {
    const filters = [];

    for (const filter of this.currentParams.filters) {
      // Map common filter types to GraphQL format
      // Shopify's filter format varies by filter type
      if (filter.name === "v.availability") {
        if (filter.value === "1" || filter.value === "in-stock") {
          filters.push({ available: true });
        }
      } else if (filter.name === "v.price.gte") {
        filters.push({ price: { min: parseFloat(filter.value) } });
      } else if (filter.name === "v.price.lte") {
        filters.push({ price: { max: parseFloat(filter.value) } });
      } else if (filter.name.startsWith("v.option.")) {
        // Variant option filters
        const optionName = filter.name.replace("v.option.", "");
        filters.push({
          variantOption: { name: optionName, value: filter.value },
        });
      } else if (filter.name === "p.vendor") {
        filters.push({ productVendor: filter.value });
      } else if (filter.name === "p.product_type") {
        filters.push({ productType: filter.value });
      } else if (filter.name === "p.tag") {
        filters.push({ tag: filter.value });
      }
    }

    return filters;
  }

  /**
   * Generate a unique cache key from URL parameters
   * Normalizes parameter order for consistent keys
   * @param {string} [url] - URL to generate key from
   * @param {string} [gridId] - Grid ID for grid-specific caching
   * @param {string} [feedType] - Feed type: "collection" or "recommendation"
   * @param {string} [feedIdentifier] - Collection handle or product ID depending on feed type
   * @returns {string} Cache key
   */
  generateCacheKey(url = window.location.href, gridId = null, feedType = null, feedIdentifier = null) {
    const parsed = new URL(url, window.location.origin);
    const params = new URLSearchParams(parsed.search);

    // Remove page param - we cache all products regardless of pagination
    params.delete("page");

    // Sort params alphabetically for consistent key
    const sortedParams = new URLSearchParams([...params.entries()].sort());

    const queryString = sortedParams.toString();
    const baseKey = `cache:${parsed.pathname}${queryString ? "?" + queryString : ""}`;

    // If gridId and feed info provided, create feed-specific key
    if (gridId && feedType && feedIdentifier) {
      return `${baseKey}:${gridId}:${feedType}:${feedIdentifier}`;
    }

    // Legacy format for backward compatibility
    return baseKey;
  }

  /**
   * Check if URL parameters have changed (excluding page)
   * @param {string} newUrl
   * @returns {boolean}
   */
  hasParamsChanged(newUrl) {
    const oldKey = this.generateCacheKey();
    const newKey = this.generateCacheKey(newUrl);
    return oldKey !== newKey;
  }

  /**
   * Update internal state with new URL
   * @param {string} [url]
   */
  update(url = window.location.href) {
    this.currentParams = this.parse(url);
  }

  /**
   * Build URL with updated parameters
   * @param {Object} updates - Parameters to update
   * @returns {string} New URL string
   */
  buildURL(updates = {}) {
    const url = new URL(window.location.href);
    const params = url.searchParams;

    if (updates.sort !== undefined) {
      if (updates.sort) {
        params.set("sort_by", updates.sort);
      } else {
        params.delete("sort_by");
      }
    }

    if (updates.page !== undefined) {
      if (updates.page > 1) {
        params.set("page", updates.page);
      } else {
        params.delete("page");
      }
    }

    if (updates.filters !== undefined) {
      // Remove existing filters
      for (const key of [...params.keys()]) {
        if (key.startsWith("filter.")) {
          params.delete(key);
        }
      }
      // Add new filters
      for (const filter of updates.filters) {
        params.set(`filter.${filter.name}`, filter.value);
      }
    }

    return `${url.pathname}${params.toString() ? "?" + params.toString() : ""}`;
  }

  /**
   * Get current collection handle
   * @returns {string|null}
   */
  get collectionHandle() {
    return this.currentParams.collectionHandle;
  }

  /**
   * Get current sort value
   * @returns {string|null}
   */
  get sort() {
    return this.currentParams.sort;
  }

  /**
   * Get current filters
   * @returns {Array}
   */
  get filters() {
    return this.currentParams.filters;
  }
}

// Export for module usage
if (typeof module !== "undefined" && module.exports) {
  module.exports = URLStateManager;
}
